import { Fragment, useId, useMemo, useState, type ReactNode } from 'react';
import { format, parseISO } from 'date-fns';
import { ChevronDown, ChevronRight, Columns3, List } from 'lucide-react';
import { LwTable, LwSortableHeader, LwTableToolbar, LwSearchInput, LwStatusPill, LW_DS_CLASS } from '@/components/lw';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { RecaptureActivity } from '@/services/recaptureService';
import type { MyCommissionsPreference } from '@/types/services';
import { useCommissionRecordPreferences } from './CommissionRecordPreferences';
import { settlementPaymentStatus } from '../../../supabase/functions/_shared/payout-settlement';
import {
  channelLabel, computeEarningConcessionBreakdown, computeDisplayedEarningLoProfitability, earningBalance,
  earningSettlement, groupCommissionEarnings, isPreJulyResetProfitabilityRow, splitsFor,
  type CommissionContextRow, type CommissionEarning
} from './myCommissionReportModel';
import { EarningDetail } from './CommissionEarningTieOut';
import { commissionMoney as money, commissionCompactMoney, commissionDate } from './commissionPresentation';

type Column = 'recipient' | 'loan' | 'funded' | 'purchased' | 'paid' | 'amount' | 'channel' | 'concession' | 'comp' | 'earned' | 'status' | 'balance' | 'profit' | 'source';
const columnNames: Record<Column, string> = {
  recipient: 'Recipient', source: 'Source officer', loan: 'Loan / Borrower', funded: 'Funded', purchased: 'Purchased',
  paid: 'Latest Paid', amount: 'Loan Amount', channel: 'Channel', concession: 'Company Concession', comp: 'Comp', earned: 'Net Earned',
  status: 'Status', balance: 'Balance', profit: 'LO Profitability'
};
const optional: Column[] = ['purchased', 'paid', 'comp', 'source'];
const numeric = new Set<Column>(['amount', 'concession', 'comp', 'earned', 'balance', 'profit']);
const copy = (value: string) => {
  void navigator.clipboard?.writeText(value).catch(() => undefined);
};

function compAmount(e: CommissionEarning): number | null {
  return e.payout_type !== 'banked' || !e.comp_basis ? null : e.comp_basis.compensation_type === 'flat_fee'
    ? e.comp_basis.compensation_value : e.loan_amount * e.comp_basis.compensation_value / 100;
}

function paymentState(e: CommissionEarning): { label: string; tone: 'success' | 'warning' | 'neutral' } {
  const settlement = earningSettlement(e);
  const paid = e.payout_type === 'volume_bonus' ? settlement.remaining <= 0 : settlementPaymentStatus(settlement) === 'paid';
  const owed = earningBalance(e) > (e.payout_type === 'volume_bonus' ? 0 : 0.01);
  return paid ? { label: settlement.recaptured > 0 ? 'Settled' : 'Paid', tone: 'success' }
    : earningBalance(e) < 0 ? { label: 'Credit', tone: 'neutral' }
      : owed ? { label: e.payout_type === 'volume_bonus' && settlement.cash > 0 ? 'Partially paid' : 'Owed', tone: 'warning' }
        : { label: 'Settled', tone: 'neutral' };
}

interface CommissionRecordsTableProps {
  earnings: CommissionEarning[];
  context: CommissionContextRow[];
  showParticipants?: boolean;
  showSplits?: boolean;
  maskResetPricingEvidence?: boolean;
  recipientNameFor?: (userId: string | null | undefined) => string;
  recaptureRecords: RecaptureActivity[] | null;
  recipientId?: string;
}

export function CommissionRecordsTable(props: CommissionRecordsTableProps) {
  const preferences = useCommissionRecordPreferences();
  return preferences.ready
    ? <RecordsTable {...props} initialPreferences={preferences.value} onPreferencesChange={preferences.save} />
    : <p role="status" className="text-sm text-muted-foreground">Loading record preferences…</p>;
}

function RecordsTable({ earnings, context, showParticipants = false, showSplits = false,
  maskResetPricingEvidence = false, recipientNameFor, recaptureRecords, recipientId, initialPreferences, onPreferencesChange }:
  CommissionRecordsTableProps & { initialPreferences: MyCommissionsPreference; onPreferencesChange?: (value: MyCommissionsPreference) => void }) {
  const id = useId();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [preferences, setPreferences] = useState(initialPreferences);
  const grouped = preferences.groupByMonth;
  const visible = new Set(preferences.columns);
  const updatePreferences = (next: MyCommissionsPreference) => {
    setPreferences(next);
    onPreferencesChange?.(next);
  };
  const [sort, setSort] = useState<{ key: Column; descending: boolean }>({ key: 'funded', descending: true });
  const columns = (Object.keys(columnNames) as Column[]).filter(key => (showParticipants || (key !== 'recipient' && key !== 'source'))
    && (!optional.includes(key) || visible.has(key)));
  const masked = (e: CommissionEarning) => maskResetPricingEvidence && isPreJulyResetProfitabilityRow(e);
  const concession = (e: CommissionEarning) => masked(e) ? null : computeEarningConcessionBreakdown(e).totalConcessionDollars;
  const value = (e: CommissionEarning, key: Column): number | string | null => {
    switch (key) {
      case 'recipient': return recipientNameFor?.(e.lo_user_id) ?? null;
      case 'loan': return `${e.loan_number} ${e.borrower_name ?? ''}`;
      case 'source': return e.source_officer_name ?? null;
      case 'funded': return e.fund_date;
      case 'purchased': return e.purchase_date ?? e.finalized_at;
      case 'paid': return e.payment_date;
      case 'amount': return e.payout_type === 'volume_bonus' ? null : e.loan_amount;
      case 'channel': return channelLabel(e.payout_type);
      case 'concession': return concession(e);
      case 'comp': return compAmount(e);
      case 'earned': return e.net_amount;
      case 'status': return paymentState(e).label;
      case 'balance': return earningBalance(e);
      case 'profit': return computeDisplayedEarningLoProfitability(e, recaptureRecords, recipientId);
    }
  };
  const roots = useMemo(() => groupCommissionEarnings(earnings), [earnings]);
  const query = search.trim().toLocaleLowerCase('en-US');
  const filtered = roots.filter(({ earning }) => `${earning.loan_number} ${earning.borrower_name ?? ''}`.toLocaleLowerCase('en-US').includes(query));
  const month = (e: CommissionEarning) => e.fund_date?.slice(0, 7) ?? '';
  const rows = filtered.map((row, index) => ({ ...row, index })).sort((a, b) => {
    if (grouped && month(a.earning) !== month(b.earning)) {
      return month(b.earning).localeCompare(month(a.earning));
    }
    const left = value(a.earning, sort.key);
    const right = value(b.earning, sort.key);
    if (left === null || left === undefined) {
      return right === null || right === undefined ? a.index - b.index : 1;
    }
    if (right === null || right === undefined) {
      return -1;
    }
    const compared = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right));
    return (sort.descending ? -compared : compared) || a.index - b.index;
  });
  const toggle = (key: string) => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    return next;
  });
  const cell = (e: CommissionEarning, key: Column): ReactNode => {
    const amount = value(e, key);
    if (key === 'loan') {
      return <><button type="button" className="mc-copy mc-loan" aria-label={`Copy loan number ${e.loan_number}`} onClick={event => {
        event.stopPropagation(); copy(e.loan_number);
      }}>{e.loan_number}</button><small>{e.borrower_name ?? '—'}</small></>;
    }
    if (key === 'amount' && amount !== null) {
      return <button type="button" className="mc-copy" aria-label={`Copy loan amount ${e.loan_amount.toFixed(2)}`} onClick={event => {
        event.stopPropagation(); copy(e.loan_amount.toFixed(2));
      }}>{commissionCompactMoney(e.loan_amount)}</button>;
    }
    if (key === 'channel') {
      return <span className={`mc-channel mc-channel-${e.payout_type}`}>{channelLabel(e.payout_type)}</span>;
    }
    if (key === 'status') {
      return <LwStatusPill {...paymentState(e)} />;
    }
    if (key === 'funded' || key === 'purchased' || key === 'paid') {
      return commissionDate(amount as string | null);
    }
    if (key === 'concession') {
      return amount === null || Number(amount) <= 0.01 ? '—' : <><span className="mc-warning">{money(Number(amount))}</span>{e.loan_amount > 0 && <small>{(Number(amount) / e.loan_amount * 10000).toFixed(1)} bps</small>}</>;
    }
    if (key === 'comp') {
      return amount === null ? '—' : <>{money(Number(amount))}{e.loan_amount > 0 && <small>{(Number(amount) / e.loan_amount * 10000).toFixed(1)} bps</small>}</>;
    }
    if (numeric.has(key)) {
      return amount === null ? '—' : <span className={Number(amount) < 0 ? 'mc-negative' : key === 'profit' ? 'mc-positive' : key === 'balance' && Number(amount) > 0.01 ? 'mc-warning' : ''}>{money(Number(amount))}</span>;
    }
    return amount ?? '—';
  };
  return <div className="mc-records">
    <LwTable presentation="canonical" density="compact" ariaLabel="Commission records" scrollRegionLabel="Commission records table"
      toolbar={<LwTableToolbar label="Commission record controls" primary={<>
        <LwSearchInput value={search} onChange={setSearch} ariaLabel="Search loan number or borrower" placeholder="Search loan number or borrower" />
        <label className="mc-mobile-sort">Sort records
          <select aria-label="Sort records" value={`${sort.key}:${sort.descending ? 'desc' : 'asc'}`} onChange={event => {
            const [key, direction] = event.target.value.split(':');
            setSort({ key: key as Column, descending: direction === 'desc' });
          }}>{columns.flatMap(key => ['desc', 'asc'].map(direction => <option key={`${key}:${direction}`} value={`${key}:${direction}`}>{columnNames[key]} · {direction === 'desc' ? 'Descending' : 'Ascending'}</option>))}</select>
        </label>
        <Button variant={grouped ? 'default' : 'outline'} className={grouped ? 'border border-primary' : undefined} size="sm" aria-pressed={grouped} onClick={() => updatePreferences({ ...preferences, groupByMonth: !grouped })}><List aria-hidden="true" />Group by month</Button>
        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm"><Columns3 aria-hidden="true" />Columns <span className="mc-count">{columns.length}</span></Button></DropdownMenuTrigger>
          <DropdownMenuContent className={`${LW_DS_CLASS} my-commissions mc-menu`} align="start">
            {optional.filter(key => key !== 'source' || showParticipants).map(key => <DropdownMenuCheckboxItem key={key} checked={visible.has(key)} onSelect={event => event.preventDefault()} onCheckedChange={checked => {
              const next = new Set(visible); if (checked) {
                next.add(key);
              } else {
                next.delete(key);
              }
              updatePreferences({ ...preferences, columns: [...next] });
            }}>{columnNames[key]}</DropdownMenuCheckboxItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </>} actions={<span className="mc-record-count" aria-live="polite">{filtered.length} of {roots.length} records</span>} />}>
      <thead><tr><th scope="col"><span className="sr-only">Expand</span></th>{columns.map(key => <LwSortableHeader key={key} label={columnNames[key]}
        direction={sort.key === key ? sort.descending ? 'descending' : 'ascending' : false} align={numeric.has(key) ? 'right' : 'left'}
        onSort={() => setSort(previous => ({ key, descending: previous.key === key ? !previous.descending : key !== 'loan' && key !== 'recipient' }))} />)}</tr></thead>
      <tbody>{rows.length === 0 && <tr><td colSpan={columns.length + 1} className="mc-empty">{earnings.length ? 'No records match your search.' : 'No commissions in this period.'}</td></tr>}
        {rows.map(({ earning: e, installments }, index) => <Fragment key={e.payout_id}>
          {grouped && (index === 0 || month(rows[index - 1].earning) !== month(e)) && <tr className="mc-month"><th colSpan={columns.length + 1} scope="rowgroup"><span>{e.fund_date ? format(parseISO(e.fund_date), 'MMMM yyyy') : 'No funded date'}</span></th></tr>}
          <tr className="mc-record" onClick={() => toggle(e.payout_id)}>
            <td className="mc-expand-cell"><button type="button" className="mc-expand" aria-label={`Tie-out for ${e.loan_number}${showParticipants ? `, ${recipientNameFor?.(e.lo_user_id) ?? 'Recipient'}` : ''}`} aria-expanded={expanded.has(e.payout_id)} aria-controls={`${id}-${e.payout_id}`}
              onClick={event => {
                event.stopPropagation(); toggle(e.payout_id);
              }}>{expanded.has(e.payout_id) ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}</button></td>
            {columns.map(key => <td key={key} data-label={columnNames[key]} className={`mc-cell-${key}${numeric.has(key) ? ' is-right' : ''}`}>{cell(e, key)}</td>)}
          </tr>
          {installments.map(installment => <tr key={installment.payout_id} className="mc-installment">
            <td colSpan={columns.length - 3}><strong>Volume Bonus{installment.volume_bonus ? ` · ${installment.volume_bonus.installment_number} of ${installment.volume_bonus.loan_count}` : ''}</strong> <span>{money(installment.payment_amount ?? 0)} cash paid · {installment.loan_number}</span></td>
            <td data-label="Net Earned" className="is-right">{money(installment.net_amount)}</td><td data-label="Status"><LwStatusPill {...paymentState(installment)} /></td>
            <td data-label="Balance" className="is-right">{money(earningBalance(installment))}</td><td data-label="LO Profitability" className="is-right">—</td>
          </tr>)}
          {expanded.has(e.payout_id) && <tr className="mc-expanded-row" id={`${id}-${e.payout_id}`}><td colSpan={columns.length + 1}>
            <EarningDetail e={e} showSplits={showSplits} maskPricingEvidence={masked(e)} splits={showSplits ? splitsFor(context, e.lo_user_id, e.loan_number) : []} recaptureRecords={recaptureRecords} recipientId={recipientId} />
          </td></tr>}
        </Fragment>)}
      </tbody>
    </LwTable>
  </div>;
}
